import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import { Empty, Table } from 'antd';
import flatMap from 'lodash/flatMap';

import queries from 'graph/queries';
import TitlePanel from 'common/TitlePanel';
import Panel from 'common/Panel';
import ContentCard from 'common/ContentCard';
import Tabs from 'common/Tabs';
import { Content } from 'scenes/Education';
import TaskDetail from 'scenes/TaskDetail';
import { CompletedQuestionnaire } from 'scenes/Questionnaire';
import { orderByDate } from 'utils/carePlans';
import EducationIcon from '../../../assets/svg/EducationIcon';
import get from 'lodash/get';
import QuestionnaireIcon from '../../../assets/svg/QuestionnaireIcon';
import { getDateFromUTCString } from 'utils/time';
import { taskTabPanelHeading } from 'utils/taskTabPanel';
import { focusFirstTableDataRow } from 'utils/focusTaskTable';

function History({ loading, carePlans }) {
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedTab, setSelectedTab] = useState('exercise');
  const [selectedActivity, setSelectedActivity] = useState(null);

  const columns = [
    {
      title: 'Task Name',
      dataIndex: 'title',
      key: 'title',
      sorter: sortByTitle,
      sortOrder: sortedInfo.columnKey === 'title' && sortedInfo.order,
      render: renderRow,
    },
  ];

  const matchForSelected = selectedActivity
    ? { params: { taskId: selectedActivity.id } }
    : null;

  const focusFirstTaskRow = useCallback(() => {
    requestAnimationFrame(() => focusFirstTableDataRow('scene-education'));
  }, []);

  return (
    <>
      <TitlePanel className="pa3 flex items-center justify-center">
        <h1 className="tracked mb0">Completed Tasks</h1>
      </TitlePanel>
      {selectedActivity ? (
        <div key={`${selectedActivity.type}-${selectedActivity.id}`}>
          {selectedActivity.type === 'education' && (
            <Content
              match={matchForSelected}
              onBack={() => setSelectedActivity(null)}
              getNextActivity={() => null}
              disableCompletion
            />
          )}
          {selectedActivity.type === 'exercise' && (
            <TaskDetail
              match={matchForSelected}
              onBack={() => setSelectedActivity(null)}
              getNextActivity={() => null}
              disableCompletion
            />
          )}
          {selectedActivity.type === 'questionnaire' && (
            <CompletedQuestionnaire
              match={matchForSelected}
              carePlanActivityId={selectedActivity.id}
              onBack={() => setSelectedActivity(null)}
            />
          )}
        </div>
      ) : (
        <>
          <Tabs
            tabs={[
              {
                label: 'Exercises',
                name: 'exercise',
              },
              {
                label: 'Questionnaires',
                name: 'questionnaire',
              },
              {
                label: 'Education',
                name: 'education',
              },
            ]}
            selectedTab={selectedTab}
            handleTabChange={handleTabChange}
            tabPanelId="scene-education"
            tablistLabel="Completed tasks"
            onEnterMoveToPanelContent={focusFirstTaskRow}
          />
          <Panel
            id="scene-education"
            role="tabpanel"
            aria-labelledby={`tab-${selectedTab}`}
            className="flex flex-column"
          >
            <div className="w-100 overflow-auto pa3-ns pa0">
              <h2 className="f5 fw6 tracked ma0 mb2 ws-normal">
                {taskTabPanelHeading(selectedTab, 'history')}
              </h2>
              {!loading && getData().length === 0 ? (
                renderEmptyText()
              ) : (
                <Table
                  onRow={(record) => {
                    const onActivate = handleRowClick(record);
                    return {
                      onClick: onActivate,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onActivate(e);
                        }
                      },
                      tabIndex: 0,
                    };
                  }}
                  columns={columns}
                  dataSource={getData()}
                  pagination={false}
                  loading={loading}
                  onChange={handleChange}
                  className="pointer"
                />
              )}
            </div>
          </Panel>
        </>
      )}
    </>
  );

  function getData() {
    if (loading) return [];
    // TODO: Edit graphQL schema so that care plans can be filtered by status so that they only return 'Active' care plans
    const activeCarePlans = carePlans.filter((cp) => cp.status === 'active');
    const orderedCarePlans = orderByDate(activeCarePlans, 'startDate', 'desc');
    const carePlanActivities = flatMap(orderedCarePlans, 'carePlanActivities');

    const activities = carePlanActivities.map((activity) => ({
      ...activity,
      key: activity.id,
      type: activity.plannable.__typename.toLowerCase(),
      title: activity.plannable.title,
      description: activity.plannable.description,
    }));
    const filteredActivities = activities.filter((activity) => activity.type === selectedTab);
    return orderByDate(filteredActivities, 'updatedAt', 'desc');
  }

  function handleRowClick(record) {
    return (e) => {
      e.preventDefault();
      setSelectedActivity(record);
    };
  }

  function handleChange(pagination, filters, sorter) {
    setSortedInfo(sorter);
  }

  function handleTabChange(tabName) {
    setSelectedTab(tabName);
    setSelectedActivity(null);
  }

  function sortByTitle(a, b) {
    const nameA = a.title.toUpperCase(); // ignore upper and lowercase
    const nameB = b.title.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  }

  function renderEmptyText() {
    const emptyCopy = {
      exercise: "You don't have any exercises yet.",
      questionnaire: "You don't have any questionnaires yet.",
      education: "You don't have any educational materials yet.",
    };
    const primary = emptyCopy[selectedTab] || emptyCopy.exercise;
    return (
      <div className="pa4 tc">
        <p className="ws-normal mb2 ma0">{primary}</p>
        <p className="ma0">{"We'll let you know when you do."}</p>
      </div>
    );
  }

  function renderRow(title, record) {
    const date = getDateFromUTCString(record.updatedAt).format('MM/DD/YYYY');
    const { type } = record;
    let icon;
    switch (type) {
      case 'education':
        icon = <EducationIcon width="4em" height="4em" />;
        break;
      case 'exercise':
        icon = get(record.plannable, 'photoUrl') ? (
          <img src={record.plannable.photoUrl} alt="exercise icon" className="mw-100 pa3" />
        ) : (
          <i className="primary f1 icon-dumbbells" />
        );
        break;
      case 'questionnaire':
        icon = <QuestionnaireIcon width="4em" height="4em" />;
        break;
      case 'missing':
        icon = <img src={Empty.PRESENTED_IMAGE_SIMPLE} alt="missing" />;
        break;
      default:
        icon = <noscript />;
    }
    return (
      <ContentCard
        icon={icon}
        title={title}
        footer={
          record.status === 'not_started' ? (
            <p style={{ marginBottom: 0 }}>{`Assigned on: ${date}`}</p>
          ) : (
            <p style={{ marginBottom: 0 }}>{`Completed on: ${date}`}</p>
          )
        }
      />
    );
  }
}

History.propTypes = {
  loading: PropTypes.bool,
  carePlans: PropTypes.array,
};

History.defaultProps = {
  carePlans: [],
};

export default compose(
  graphql(queries.carePlansComplete, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data: { loading, carePlans } }) => ({
      carePlans,
      loading,
    }),
  }),
)(History);
